import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CaretLeft, CaretRight, MagnifyingGlassPlus } from "@phosphor-icons/react";
import { PORTFOLIO } from "@/lib/siteData";

export const Portfolio = () => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const openAt = (i) => {
    setActive(i);
    setOpen(true);
  };
  const close = () => setOpen(false);
  const prev = useCallback(
    () => setActive((v) => (v - 1 + PORTFOLIO.length) % PORTFOLIO.length),
    []
  );
  const next = useCallback(
    () => setActive((v) => (v + 1) % PORTFOLIO.length),
    []
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open, prev, next]);

  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="relative bg-[#1a1a1a] py-20 md:py-28"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="w-10 h-[2px] bg-[#c41e3a]" />
              <span className="font-mono-upper text-[11px] text-[#c41e3a]">
                Recent Work
              </span>
            </div>
            <h2 className="font-display font-black text-white text-4xl md:text-6xl leading-[1] tracking-[-0.03em] max-w-2xl">
              Proof in every
              <span className="block text-[#c41e3a]">square foot.</span>
            </h2>
          </div>
          <p className="font-body text-white/65 text-base md:text-lg max-w-sm leading-relaxed">
            A snapshot of commercial, residential, and industrial jobs completed
            across Saskatchewan. Click any image to enlarge.
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {PORTFOLIO.map((src, i) => (
            <motion.button
              type="button"
              key={src}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
              onClick={() => openAt(i)}
              data-testid={`portfolio-item-${i}`}
              className="portfolio-item group relative aspect-[4/3] bg-[#242424] overflow-hidden focus:outline-none"
              aria-label={`View portfolio image ${i + 1}`}
            >
              <img
                src={src}
                alt={`Portfolio ${i + 1}`}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <span className="absolute inset-0 bg-[#1a1a1a]/0 group-hover:bg-[#1a1a1a]/50 transition-colors" />
              <span className="absolute top-3 left-3 font-mono-upper text-[10px] text-white/85">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="w-12 h-12 bg-[#c41e3a] flex items-center justify-center">
                  <MagnifyingGlassPlus size={20} weight="bold" className="text-white" />
                </span>
              </span>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            data-testid="portfolio-lightbox"
            className="fixed inset-0 z-[100] bg-[#0a0a0a]/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
            onClick={close}
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                close();
              }}
              aria-label="Close"
              data-testid="portfolio-lightbox-close"
              className="absolute top-5 right-5 w-11 h-11 bg-[#c41e3a] hover:bg-[#e62244] text-white flex items-center justify-center transition-colors"
            >
              <X size={18} weight="bold" />
            </button>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                prev();
              }}
              aria-label="Previous"
              data-testid="portfolio-lightbox-prev"
              className="absolute left-4 md:left-10 top-1/2 -translate-y-1/2 w-12 h-12 border border-white/20 hover:bg-[#c41e3a] hover:border-[#c41e3a] text-white flex items-center justify-center transition-colors"
            >
              <CaretLeft size={20} weight="bold" />
            </button>

            <motion.img
              key={active}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35 }}
              src={PORTFOLIO[active]}
              alt={`Portfolio ${active + 1}`}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] max-w-[92vw] object-contain shadow-[0_0_0_4px_#c41e3a]"
              data-testid="portfolio-lightbox-image"
            />

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                next();
              }}
              aria-label="Next"
              data-testid="portfolio-lightbox-next"
              className="absolute right-4 md:right-10 top-1/2 -translate-y-1/2 w-12 h-12 border border-white/20 hover:bg-[#c41e3a] hover:border-[#c41e3a] text-white flex items-center justify-center transition-colors"
            >
              <CaretRight size={20} weight="bold" />
            </button>

            <div
              className="absolute bottom-6 left-1/2 -translate-x-1/2 font-mono-upper text-[11px] text-white/75"
              onClick={(e) => e.stopPropagation()}
            >
              {String(active + 1).padStart(2, "0")} / {String(PORTFOLIO.length).padStart(2, "0")}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Portfolio;
